import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PERMISSIONS } from 'modules/auth/permissions';
import { AlertCircle, ChevronRight, History as HistoryIcon, Search } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Skeleton } from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import { SegmentedTabs, TableSearch } from 'components/ui/data-table';
import { DateRangeFilter } from 'components/ui/date-range-filter';
import { FilterButton, FilterReset, type FilterOption } from 'components/ui/filter-button';
import { TablePager } from 'components/ui/pagination';
import { StatTile } from 'components/ui/stat-tile';
import Avatar from 'components/ui/avatar';
import { AttemptBadge, AttemptScore } from 'components/history/AttemptStatus';
import { HistoryMobile } from 'components/mobile/screens/HistoryMobile';
import * as AuthModule from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { isAccessContextReady } from 'modules/auth/access';
import * as ResultModule from 'modules/result';
import { canOpenResult } from 'modules/result/access';
import * as TestModule from 'modules/test';
import * as Teams from 'modules/teams';
import { useFeature, FEATURES } from 'modules/entitlements';
import { cn } from 'lib/utils';
import { useIsMobile } from 'hooks/use-media-query';
import { usePaginationParams } from 'hooks/use-pagination-params';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { formatDayTime, formatGroupedNumber } from 'lib/dateFormat';
import type { DateRange } from 'lib/dateRange';
import { formatClockDuration } from 'lib/timeFormat';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'components/ui/page-header';
import { LoadingScreen } from 'components/ui/LoadingScreen';
import type { IEntity } from 'modules/result/types';

/* ── Helpers ─────────────────────────────────────────── */

type StatusFilter = 'all' | 'completed' | 'in_progress' | 'abandoned';
type Attempt = IEntity.Result;
type Translate = (key: string, opts?: Record<string, unknown>) => string;

/* ── Summary Stats ───────────────────────────────────── */

interface SummaryStatsProps {
  stats: IEntity.MyStats;
  t: Translate;
}

/** Plain figures in ink, as on the board; colour stays for statuses. */
const SummaryStats: React.FC<SummaryStatsProps> = React.memo(({ stats, t }) => {
  const cards = [
    { label: t('total_attempts'), value: formatGroupedNumber(stats.totalAttempts) },
    { label: t('avg_score'), value: `${stats.avgScore}%` },
    ...(stats.passRate != null ? [{ label: t('pass_rate'), value: `${stats.passRate}%` }] : []),
    { label: t('avg_duration'), value: formatClockDuration(stats.avgDuration) },
  ];

  return (
    <div className={cn('grid grid-cols-2 gap-3', stats.passRate == null ? 'lg:grid-cols-3' : 'lg:grid-cols-4')}>
      {cards.map((c) => (
        <StatTile key={c.label} label={c.label} value={c.value} />
      ))}
    </div>
  );
});
SummaryStats.displayName = 'SummaryStats';

const LoadingRows: React.FC = () => (
  <div className="divide-y divide-border" aria-hidden>
    {[0, 1, 2, 3, 4, 5].map((row) => (
      <div key={row} className="flex items-center gap-6 px-4 py-3.5">
        <Skeleton className={cn('h-3.5', row % 2 ? 'w-2/5' : 'w-1/2')} />
        <span className="flex-1" />
        <Skeleton className="hidden h-3.5 w-24 md:block" />
        <Skeleton className="h-3.5 w-10" />
        <Skeleton className="h-5 w-16" />
      </div>
    ))}
  </div>
);

/* ── Main Page ───────────────────────────────────────── */

const HistoryDesktop: React.FC = () => {
  const { t } = useTranslation();
  const { state: authState } = AuthModule.Context.useAuthContext();
  const { user: profile } = authState;
  const userId = profile?.id ?? '';
  const accessReady = isAccessContextReady(authState);

  // Staff (admins, authors, proctors) get org-wide history + advanced filters.
  // Candidates lack `assessment.view_results`, so they only see their own.
  const canViewOrg = usePermission(PERMISSIONS.TESTS_VIEW_RESULTS);

  const { page, pageSize, setPage, setPageSize } = usePaginationParams({
    defaultPage: 1,
    defaultPageSize: 10,
    pageSizeParamKey: 'pageSize',
  });

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const { input: searchInput, setInput: setSearchInput, query: search } = useSearchParamState();
  const [teamFilter, setTeamFilter] = useState<string | undefined>();
  const [testFilter, setTestFilter] = useState<string | undefined>();
  const [dates, setDates] = useState<DateRange>({});

  // A filter change returns to page 1 — but not on first render, so a
  // deep-linked ?page= survives the initial load.
  const filterKey = [statusFilter, search, teamFilter, testFilter, dates.from, dates.to].join('|');
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    setPage(1);
  }, [filterKey, setPage]);

  /* ── Filter option sources (loaded the first time their popover opens) ── */
  const hasTeams = useFeature(FEATURES.TEAMS);
  const [teamsOpened, setTeamsOpened] = useState(false);
  const [testsOpened, setTestsOpened] = useState(false);
  const { teams, isLoading: teamsLoading } = Teams.Hooks.useList({
    enabled: accessReady && canViewOrg && hasTeams && (teamsOpened || Boolean(teamFilter)),
  });
  const teamOptions = useMemo<FilterOption[]>(
    () => (teams ?? []).map((team) => ({ value: team.id, label: team.name })),
    [teams],
  );

  const listParams = useMemo<ResultModule.Types.IApi.List.Params | undefined>(() => {
    if (!userId) return undefined;
    const params: ResultModule.Types.IApi.List.Params = {
      page,
      perPage: pageSize,
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(search ? { q: search } : {}),
    };
    if (testFilter) params.testId = testFilter;
    if (canViewOrg) {
      if (teamFilter) params.teamId = teamFilter;
      if (dates.from) params.dateFrom = `${dates.from}T00:00:00`;
      if (dates.to) params.dateTo = `${dates.to}T23:59:59`;
    }
    return params;
  }, [userId, page, pageSize, statusFilter, search, testFilter, canViewOrg, teamFilter, dates]);

  const { items, meta, isLoading, isPlaceholderData, isError, refetch } = ResultModule.Hooks.useList({
    params: listParams,
    enabled: accessReady && !!userId,
    keepPrevious: true,
  });
  const totalPages = meta.totalPages || 0;

  const loadTests = accessReady && (testsOpened || Boolean(testFilter));
  const { items: testsList = [], isLoading: testsLoading } = TestModule.Hooks.useList({
    params: { perPage: 100 },
    enabled: loadTests && Boolean(canViewOrg),
  });
  const { items: availableTests = [], isLoading: availableLoading } = TestModule.Hooks.useAvailableTests({
    params: { perPage: 100 },
    enabled: loadTests && !canViewOrg,
  });

  // Tests the list can already name (from the rows) come first-class, so a
  // selected test keeps its label before the full list has loaded.
  const testOptions = useMemo<FilterOption[]>(() => {
    const map = new Map<string, string>();
    const tests = canViewOrg ? testsList : availableTests;
    for (const test of tests ?? []) {
      if (test.id && test.title) map.set(test.id, test.title);
    }
    for (const item of items ?? []) {
      if (item.testId && item.testTitle && !map.has(item.testId)) {
        map.set(item.testId, item.testTitle);
      }
    }
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [canViewOrg, testsList, availableTests, items]);

  // Personal summary stats only make sense in the candidate (own-history) view.
  const { stats, isLoading: statsLoading } = ResultModule.Hooks.useMyStats({
    enabled: accessReady && !canViewOrg && !!userId,
  });

  const hasFilters =
    Boolean(searchInput.trim() || testFilter) || (canViewOrg && Boolean(teamFilter || dates.from || dates.to));
  const isFiltering = statusFilter !== 'all' || hasFilters;
  const showStats = !canViewOrg && !statsLoading && stats.totalAttempts > 0;

  const handleClearFilters = () => {
    setStatusFilter('all');
    setSearchInput('');
    setTeamFilter(undefined);
    setTestFilter(undefined);
    setDates({});
  };

  const statusTabs: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: t('all') },
    { value: 'completed', label: t('completed') },
    { value: 'in_progress', label: t('in_progress') },
    { value: 'abandoned', label: t('abandoned') },
  ];

  if (!accessReady) {
    return <LoadingScreen fullScreen={false} />;
  }

  if (!userId) {
    return (
      <div className="space-y-6">
        <h1 className="text-surface-title font-surface-title tracking-tight text-foreground">{t('nav.my_attempts')}</h1>
        <p className="text-muted-foreground">{t('sign_in_to_see_your_assessment_history')}</p>
      </div>
    );
  }

  const attemptPath = (attempt: Attempt) => `/app/results/${attempt.id}`;

  const renderRows = () => {
    return (
      <Table className="table-fixed">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>{t('test')}</TableHead>
            {canViewOrg && <TableHead className="w-52">{t('member')}</TableHead>}
            <TableHead className="w-40">{t('date')}</TableHead>
            <TableHead className="w-24 text-right">{t('score_label')}</TableHead>
            <TableHead className="w-36">{t('status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((attempt) => (
            <TableRow key={attempt.id} className="h-[52px]">
              <TableCell className="max-w-0">
                {canOpenResult(attempt, userId) ? (
                  <Link to={attemptPath(attempt)} className="block truncate text-sm font-medium text-foreground hover:underline">
                    {attempt.testTitle}
                  </Link>
                ) : (
                  <span className="block truncate text-sm font-medium text-foreground">{attempt.testTitle}</span>
                )}
              </TableCell>
              {canViewOrg && (
                <TableCell className="max-w-0">
                  <span className="flex min-w-0 items-center gap-2">
                    <Avatar name={attempt.userName} size="xs" className="flex-shrink-0" />
                    <span className="truncate text-[13px] text-foreground">{attempt.userName || '—'}</span>
                  </span>
                </TableCell>
              )}
              <TableCell className="whitespace-nowrap text-[13px] tabular-nums text-muted-foreground">
                {formatDayTime(attempt.completedAt ?? attempt.startedAt)}
              </TableCell>
              <TableCell className="text-right text-[13.5px]">
                <AttemptScore attempt={attempt} />
              </TableCell>
              <TableCell>
                <AttemptBadge attempt={attempt} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderBody = () => {
    if (isLoading) return <LoadingRows />;
    if (isError) {
      return (
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={t('failed_to_load_history')}
          secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
        />
      );
    }
    if (items.length === 0 && isFiltering) {
      return (
        <EmptyState
          icon={<Search size={28} />}
          title={t('no_results')}
          description={t('try_adjusting_your_search_or_filters')}
          secondaryAction={{ label: t('clear_filters'), onClick: handleClearFilters }}
        />
      );
    }
    if (items.length === 0) {
      return (
        <EmptyState
          icon={<HistoryIcon size={28} />}
          title={canViewOrg ? t('no_attempts_in_organization_yet') : t('no_attempts_yet')}
          description={
            canViewOrg
              ? t('assessment_attempts_across_your_organization_will_appear_here')
              : t('when_you_complete_assessments_via_invite_or_from_a_test_link_they_will_appear_here')
          }
        />
      );
    }
    return renderRows();
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Breadcrumb — personal attempts live under the account menu */}
      {!canViewOrg && (
        <nav className="flex items-center gap-1 text-xs text-muted-foreground" aria-label={t('breadcrumb')}>
          <Link to="/app/account" className="hover:text-foreground">
            {t('account')}
          </Link>
          <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
          <span className="font-medium text-foreground">{t('nav.my_attempts')}</span>
        </nav>
      )}

      <PageHeader
        title={
          <span className="flex items-center gap-2.5">
            {canViewOrg ? t('nav.history') : t('nav.my_attempts')}
            {meta.total > 0 && (
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                {meta.total}
              </span>
            )}
          </span>
        }
        description={canViewOrg ? undefined : t('my_attempts_subtitle')}
      />

      {/* Summary Stats — personal view only */}
      {showStats && <SummaryStats stats={stats} t={t} />}

      <div className="flex flex-wrap items-center gap-2">
        <TableSearch
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('search_by_test_name')}
          aria-label={t('search_by_test_name')}
        />
        <SegmentedTabs<StatusFilter>
          aria-label={t('status')}
          options={statusTabs}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        {canViewOrg && hasTeams && (
          <FilterButton
            label={t('team')}
            options={teamOptions}
            value={teamFilter}
            onChange={setTeamFilter}
            onOpenChange={(open) => open && setTeamsOpened(true)}
            loading={teamsLoading && teamsOpened}
            searchPlaceholder={t('search')}
          />
        )}
        <FilterButton
          label={t('test')}
          options={testOptions}
          value={testFilter}
          onChange={setTestFilter}
          onOpenChange={(open) => open && setTestsOpened(true)}
          loading={(canViewOrg ? testsLoading : availableLoading) && testsOpened && testOptions.length === 0}
          searchPlaceholder={t('search')}
        />
        {canViewOrg && <DateRangeFilter label={t('date')} value={dates} onChange={setDates} />}
        {hasFilters && <FilterReset onClick={handleClearFilters} />}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {/* While the next page loads the previous one stays, dimmed, so nothing jumps. */}
        <div className={cn('transition-opacity', isPlaceholderData && 'opacity-60')} aria-busy={isPlaceholderData || undefined}>
          {renderBody()}
        </div>
        {!isLoading && !isError && meta.total > 0 && (
          <div className="border-t border-border px-4 py-3">
            <TablePager
              page={page}
              pageCount={totalPages || 1}
              pageSize={pageSize}
              total={meta.total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 20, 50, 100]}
              noun={t('pager_noun_attempts')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Phones get the dedicated History screen; the switch keeps each side's hooks stable.
const History: React.FC = () => (useIsMobile() ? <HistoryMobile /> : <HistoryDesktop />);

export default History;
